/**
 * Admin tuning panel — the permanent tool to refine per-surface driving feel
 * during playtests (it will also be embedded in the circuit workshop and league
 * admin screens in later phases).
 *
 * Since Phase 2 the physics configs live ON THE SERVER: local edits apply
 * immediately to the local prediction, then are sent (debounced) to the server,
 * which persists them and broadcasts to every connected client. The camera and
 * the network simulator sections stay purely local (they are not gameplay).
 *
 * `carRadius` is deliberately NOT exposed: collision geometry is part of
 * fairness, not feel tuning.
 */

import type { PhysicsConfig, PresetId, SurfaceConfigs, SurfaceId } from '@microligue/shared';
import {
  PHYSICS_PRESETS,
  PRESET_LABELS,
  SURFACE_IDS,
  SURFACE_LABELS,
  defaultSurfaceConfigs,
} from '@microligue/shared';
import type { NetSimSettings } from '../net/connection';
import type { CameraConfig } from './camera';
import { defaultCamera } from './camera';
import type { DisplaySettings } from './display';
import { saveDisplaySettings } from './display';
import { SURFACE_COLORS } from './surfaces';

const CAMERA_STORAGE_KEY = 'microligue.camera.v1';
const OPEN_STORAGE_KEY = 'microligue.tuning.open';
const SEND_DEBOUNCE_MS = 200;

type SliderTarget = 'physics' | 'camera' | 'netsim';

interface SliderDef {
  target: SliderTarget;
  key: string;
  label: string;
  min: number;
  max: number;
  step: number;
}

interface Section {
  title: string;
  sliders: SliderDef[];
}

const SECTIONS: Section[] = [
  {
    title: 'Moteur',
    sliders: [
      {
        target: 'physics',
        key: 'maxSpeed',
        label: 'Vitesse max (px/s)',
        min: 120,
        max: 800,
        step: 5,
      },
      {
        target: 'physics',
        key: 'accel',
        label: 'Accélération (px/s²)',
        min: 100,
        max: 1400,
        step: 10,
      },
      {
        target: 'physics',
        key: 'brakeDecel',
        label: 'Freinage (px/s²)',
        min: 200,
        max: 1800,
        step: 10,
      },
      {
        target: 'physics',
        key: 'reverseAccel',
        label: 'Accél. marche arrière (px/s²)',
        min: 50,
        max: 800,
        step: 10,
      },
      {
        target: 'physics',
        key: 'reverseMaxSpeed',
        label: 'Vitesse max marche arrière (px/s)',
        min: 40,
        max: 300,
        step: 5,
      },
    ],
  },
  {
    title: 'Direction',
    sliders: [
      {
        target: 'physics',
        key: 'turnRate',
        label: 'Taux de rotation (rad/s)',
        min: 0.5,
        max: 6,
        step: 0.05,
      },
      {
        target: 'physics',
        key: 'turnInSpeed',
        label: 'Vitesse de pleine direction (px/s)',
        min: 10,
        max: 300,
        step: 5,
      },
      {
        target: 'physics',
        key: 'highSpeedTurnDrop',
        label: 'Perte de direction à haute vitesse',
        min: 0,
        max: 0.9,
        step: 0.01,
      },
    ],
  },
  {
    title: 'Adhérence',
    sliders: [
      {
        target: 'physics',
        key: 'lateralGrip',
        label: 'Grip latéral (1/s)',
        min: 0.5,
        max: 20,
        step: 0.1,
      },
      {
        target: 'physics',
        key: 'driftThreshold',
        label: 'Seuil de glisse (px/s)',
        min: 0,
        max: 400,
        step: 5,
      },
      {
        target: 'physics',
        key: 'driftBlendBand',
        label: 'Bande de transition (px/s)',
        min: 10,
        max: 300,
        step: 5,
      },
      {
        target: 'physics',
        key: 'driftGrip',
        label: 'Grip en glisse (1/s)',
        min: 0.2,
        max: 10,
        step: 0.1,
      },
      {
        target: 'physics',
        key: 'rollingFriction',
        label: 'Friction de roulement (1/s)',
        min: 0,
        max: 3,
        step: 0.05,
      },
    ],
  },
  {
    title: 'Collisions',
    sliders: [
      {
        target: 'physics',
        key: 'wallRestitution',
        label: 'Rebond sur les murs',
        min: 0,
        max: 1,
        step: 0.01,
      },
      {
        target: 'physics',
        key: 'wallFriction',
        label: 'Friction des murs',
        min: 0,
        max: 1,
        step: 0.01,
      },
      {
        target: 'physics',
        key: 'wallScrub',
        label: 'Râpage au contact des murs (anti-appui)',
        min: 0,
        max: 0.2,
        step: 0.005,
      },
      {
        target: 'physics',
        key: 'carBounciness',
        label: 'Rebond voiture-voiture',
        min: 0,
        max: 1,
        step: 0.01,
      },
    ],
  },
  {
    title: 'Caméra (locale)',
    sliders: [
      {
        target: 'camera',
        key: 'lookAhead',
        label: 'Anticipation (s)',
        min: 0,
        max: 0.8,
        step: 0.01,
      },
      {
        target: 'camera',
        key: 'stiffness',
        label: 'Rigidité de suivi (1/s)',
        min: 1,
        max: 15,
        step: 0.1,
      },
      {
        target: 'camera',
        key: 'zoomStiffness',
        label: 'Rigidité du zoom (1/s)',
        min: 0.5,
        max: 10,
        step: 0.1,
      },
      { target: 'camera', key: 'baseZoom', label: 'Zoom de base', min: 0.4, max: 2, step: 0.02 },
      {
        target: 'camera',
        key: 'speedDezoom',
        label: 'Dézoom à haute vitesse',
        min: 0,
        max: 0.8,
        step: 0.01,
      },
    ],
  },
  {
    title: 'Réseau — simulateur (dev, local)',
    sliders: [
      {
        target: 'netsim',
        key: 'delayMs',
        label: 'Latence aller-retour ajoutée (ms)',
        min: 0,
        max: 400,
        step: 10,
      },
      { target: 'netsim', key: 'jitterMs', label: 'Gigue (ms)', min: 0, max: 100, step: 5 },
      {
        target: 'netsim',
        key: 'lossPct',
        label: 'Perte entrées/snapshots (%)',
        min: 0,
        max: 20,
        step: 1,
      },
    ],
  },
];

type NumericRecord = Record<string, number>;

function asRecord(obj: PhysicsConfig | CameraConfig | NetSimSettings): NumericRecord {
  return obj as unknown as NumericRecord;
}

export interface TuningPanelOptions {
  /** Called (debounced) when physics configs changed locally — sends to server. */
  onPhysicsChanged: (surfaces: SurfaceConfigs) => void;
  /**
   * Called to flip the WHOLE test circuit onto one surface (or null to restore
   * its real surfaces). The server applies it per-room and echoes it back.
   */
  onSurfaceOverride: (surface: SurfaceId | null) => void;
  /** Called when the local network simulator settings changed. */
  onNetSimChanged: () => void;
}

export class TuningPanel {
  private selectedSurface: SurfaceId = 'asphalt';
  private drivenSurface: SurfaceId | null = null;
  /** Server-confirmed whole-circuit surface flip (null = real surfaces). */
  private surfaceOverride: SurfaceId | null = null;
  /** True only on the builtin test track — where the flip is offered. */
  private overrideAvailable = false;
  /** Only admins may flip the circuit (the server enforces this too). */
  private canAdmin = false;
  /**
   * True while a race is underway (countdown/racing/results). The server refuses
   * the flip then — records stay on the real surfaces — so the button is locked
   * with a hint pointing the admin back to the waiting room (échauffement libre).
   */
  private raceActive = false;
  private followCar = false;
  /** True while a pointer is held inside the panel — freezes follow-car switches. */
  private pointerHeld = false;
  private pendingFollowSurface: SurfaceId | null = null;
  private pendingServerRefresh = false;
  private sendTimer: number | null = null;
  /** Deep copy of the configs as last confirmed by the server. */
  private lastServerSurfaces: SurfaceConfigs | null = null;

  private readonly inputs = new Map<string, { range: HTMLInputElement; value: HTMLElement }>();
  private readonly surfaceButtons = new Map<SurfaceId, HTMLButtonElement>();
  private readonly presetButtons = new Map<PresetId, HTMLButtonElement>();
  private surfaceDefaultButton: HTMLButtonElement | null = null;
  private flipButton: HTMLButtonElement | null = null;
  private flipHint: HTMLElement | null = null;
  private surfaceTitle: HTMLElement | null = null;
  private copySelect: HTMLSelectElement | null = null;
  private adminNotice: HTMLElement | null = null;
  private io: HTMLTextAreaElement | null = null;
  private msg: HTMLElement | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly toggleButton: HTMLElement,
    private readonly surfaces: SurfaceConfigs,
    private readonly camera: CameraConfig,
    private readonly netsim: NetSimSettings,
    /** Réglages d'affichage locaux (mode éco) — objet partagé avec NetGame. */
    private readonly display: DisplaySettings,
    private readonly options: TuningPanelOptions,
  ) {
    this.restoreCamera();
    this.build();
    this.refresh();
    const openStored = localStorage.getItem(OPEN_STORAGE_KEY);
    this.setVisible(openStored === null ? true : openStored === '1');
    this.toggleButton.addEventListener('click', () => this.toggle());

    // While the user holds a pointer in the panel (dragging a slider…), the car
    // keeps moving and may cross a surface boundary: neither follow-car nor a
    // server refresh may re-target/reset the sliders mid-gesture.
    this.root.addEventListener('pointerdown', () => {
      this.pointerHeld = true;
    });
    const endInteraction = (): void => {
      if (!this.pointerHeld) return;
      this.pointerHeld = false;
      if (this.pendingServerRefresh) {
        this.pendingServerRefresh = false;
        this.refresh();
      }
      if (this.pendingFollowSurface && this.followCar) {
        const surface = this.pendingFollowSurface;
        this.pendingFollowSurface = null;
        if (surface !== this.selectedSurface) this.selectSurface(surface);
      }
    };
    window.addEventListener('pointerup', endInteraction);
    window.addEventListener('pointercancel', endInteraction);
  }

  toggle(): void {
    this.setVisible(this.root.hidden);
  }

  /** Called with the welcome message: may this account edit the tuning? */
  setCanAdmin(canAdmin: boolean): void {
    this.canAdmin = canAdmin;
    if (this.adminNotice) this.adminNotice.hidden = canAdmin;
    this.updateFlipUi();
  }

  /** Server pushed new configs (someone else tuned, or initial welcome). */
  applyServerSurfaces(surfaces: SurfaceConfigs): void {
    for (const id of SURFACE_IDS) {
      Object.assign(this.surfaces[id], surfaces[id]);
    }
    this.lastServerSurfaces = structuredClone(this.surfaces);
    if (this.pointerHeld) {
      this.pendingServerRefresh = true;
    } else {
      this.refresh();
    }
  }

  /**
   * Server rejected our tuning (not an admin): the local prediction must NOT
   * keep driving with configs the server refused — revert to the last known
   * server state, otherwise the car rubber-bands forever. Never yank the
   * sliders out from under an in-progress drag.
   */
  notifyAdminRejected(): void {
    if (this.lastServerSurfaces) {
      for (const id of SURFACE_IDS) {
        Object.assign(this.surfaces[id], this.lastServerSurfaces[id]);
      }
      if (this.pointerHeld) this.pendingServerRefresh = true;
      else this.refresh();
    }
    this.say('Réservé aux admins — réglages non enregistrés (locaux uniquement).');
  }

  /** Called by the game every frame with the surface under the car. */
  notifyDrivenSurface(surface: SurfaceId): void {
    if (surface === this.drivenSurface) return;
    this.drivenSurface = surface;
    for (const [id, btn] of this.surfaceButtons) {
      btn.classList.toggle('driving', id === surface);
    }
    if (this.followCar && surface !== this.selectedSurface) {
      if (this.pointerHeld) {
        this.pendingFollowSurface = surface;
      } else {
        this.selectSurface(surface);
      }
    }
  }

  private setVisible(visible: boolean): void {
    this.root.hidden = !visible;
    localStorage.setItem(OPEN_STORAGE_KEY, visible ? '1' : '0');
  }

  private currentPhysics(): PhysicsConfig {
    return this.surfaces[this.selectedSurface];
  }

  private targetRecord(target: SliderTarget): NumericRecord {
    if (target === 'physics') return asRecord(this.currentPhysics());
    if (target === 'camera') return asRecord(this.camera);
    return asRecord(this.netsim);
  }

  private selectSurface(surface: SurfaceId): void {
    this.selectedSurface = surface;
    for (const [id, btn] of this.surfaceButtons) {
      btn.classList.toggle('active', id === surface);
    }
    if (this.surfaceTitle) {
      this.surfaceTitle.textContent = `Réglages — ${SURFACE_LABELS[surface]}`;
    }
    this.refresh();
    this.updateFlipUi();
  }

  /**
   * A surface tab was clicked. Always edit that surface; and while the whole
   * circuit is flipped, re-flip it onto the newly picked surface — "click mud
   * → the entire circuit becomes mud".
   */
  private onSurfaceTabClicked(id: SurfaceId): void {
    this.selectSurface(id);
    if (this.canFlip() && this.surfaceOverride !== null && id !== this.surfaceOverride) {
      this.options.onSurfaceOverride(id);
    }
  }

  /** Flip the whole circuit onto the edited surface, or restore real surfaces. */
  private toggleOverride(): void {
    if (!this.canFlip()) return;
    const target = this.surfaceOverride === null ? this.selectedSurface : null;
    this.options.onSurfaceOverride(target);
  }

  /** Server confirmed the whole-circuit flip (or its reset). */
  setSurfaceOverride(surface: SurfaceId | null): void {
    this.surfaceOverride = surface;
    // Edit what is actually driven everywhere.
    if (surface !== null && surface !== this.selectedSurface) {
      this.selectSurface(surface);
      return; // selectSurface already refreshed the flip UI.
    }
    this.updateFlipUi();
  }

  /** Only the builtin test track offers the whole-circuit flip. */
  setSurfaceOverrideAvailable(available: boolean): void {
    this.overrideAvailable = available;
    this.updateFlipUi();
  }

  /** The flip is a waiting-room tool: lock it while a race is underway. */
  setRaceActive(active: boolean): void {
    this.raceActive = active;
    this.updateFlipUi();
  }

  /** May the whole-circuit flip be used right now? */
  private canFlip(): boolean {
    return this.overrideAvailable && this.canAdmin && !this.raceActive;
  }

  private updateFlipUi(): void {
    const btn = this.flipButton;
    const hint = this.flipHint;
    if (!btn || !hint) return;
    for (const [id, b] of this.surfaceButtons) {
      b.classList.toggle('overridden', id === this.surfaceOverride);
    }
    if (!this.canFlip()) {
      btn.disabled = true;
      btn.classList.remove('active');
      btn.textContent = 'Basculer tout le circuit';
      hint.textContent = !this.canAdmin
        ? 'Réservé aux admins.'
        : !this.overrideAvailable
          ? 'Disponible sur le circuit d’essai.'
          : 'Disponible dans le salon d’attente (échauffement libre), pas en course.';
      return;
    }
    btn.disabled = false;
    if (this.surfaceOverride === null) {
      btn.classList.remove('active');
      btn.textContent = `Basculer tout le circuit sur : ${SURFACE_LABELS[this.selectedSurface]}`;
      hint.textContent = 'Le circuit garde ses revêtements réels.';
    } else {
      btn.classList.add('active');
      btn.textContent = 'Rétablir les revêtements du circuit';
      hint.textContent = `Tout le circuit roule en « ${SURFACE_LABELS[this.surfaceOverride]} ».`;
    }
  }

  /** Local change of any physics value → debounced push to the server. */
  private schedulePush(): void {
    if (this.sendTimer !== null) window.clearTimeout(this.sendTimer);
    this.sendTimer = window.setTimeout(() => {
      this.sendTimer = null;
      this.options.onPhysicsChanged(this.surfaces);
    }, SEND_DEBOUNCE_MS);
  }

  private build(): void {
    const frag = document.createDocumentFragment();

    const title = document.createElement('h2');
    title.textContent = 'Réglages de conduite';
    frag.appendChild(title);
    const sub = document.createElement('div');
    sub.className = 'tuning-sub';
    sub.textContent =
      'Partagés : appliqués en direct à tous les joueurs connectés · T pour masquer';
    frag.appendChild(sub);

    // Surface tabs
    const surfacesTitle = document.createElement('h3');
    surfacesTitle.textContent = 'Surface éditée';
    frag.appendChild(surfacesTitle);
    const tabs = document.createElement('div');
    tabs.className = 'tuning-surfaces';
    for (const id of SURFACE_IDS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      const swatch = document.createElement('span');
      swatch.className = 'surface-swatch';
      swatch.style.backgroundColor = SURFACE_COLORS[id];
      const label = document.createElement('span');
      label.textContent = SURFACE_LABELS[id];
      btn.append(swatch, label);
      btn.title = `${SURFACE_LABELS[id]} — le point indique la surface sous la voiture`;
      btn.addEventListener('click', () => this.onSurfaceTabClicked(id));
      this.surfaceButtons.set(id, btn);
      tabs.appendChild(btn);
    }
    frag.appendChild(tabs);

    // Whole-circuit flip: put the entire test track on the edited surface so it
    // can be felt and tuned everywhere. Test track only, admin only (server).
    const flip = document.createElement('button');
    flip.type = 'button';
    flip.className = 'tuning-flip';
    flip.addEventListener('click', () => this.toggleOverride());
    this.flipButton = flip;
    frag.appendChild(flip);
    const flipHint = document.createElement('div');
    flipHint.className = 'tuning-flip-hint';
    this.flipHint = flipHint;
    frag.appendChild(flipHint);

    const followRow = document.createElement('label');
    followRow.className = 'tuning-follow';
    const followBox = document.createElement('input');
    followBox.type = 'checkbox';
    followBox.addEventListener('change', () => {
      this.followCar = followBox.checked;
      if (this.followCar && this.drivenSurface) this.selectSurface(this.drivenSurface);
    });
    followRow.append(followBox, document.createTextNode(' Suivre la surface sous la voiture'));
    frag.appendChild(followRow);

    const surfaceTitle = document.createElement('div');
    surfaceTitle.className = 'tuning-surface-title';
    this.surfaceTitle = surfaceTitle;
    frag.appendChild(surfaceTitle);

    // Presets (applied to the edited surface)
    const presetsTitle = document.createElement('h3');
    presetsTitle.textContent = 'Presets (surface éditée)';
    frag.appendChild(presetsTitle);
    const presets = document.createElement('div');
    presets.className = 'tuning-presets';
    const defaultBtn = document.createElement('button');
    defaultBtn.type = 'button';
    defaultBtn.textContent = 'Défaut de la surface';
    defaultBtn.title = 'Restaure les réglages par défaut de la surface éditée (le feeling prévu)';
    defaultBtn.addEventListener('click', () => this.applySurfaceDefault());
    this.surfaceDefaultButton = defaultBtn;
    presets.appendChild(defaultBtn);
    for (const id of ['grip', 'balanced', 'drift'] as PresetId[]) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = PRESET_LABELS[id];
      btn.addEventListener('click', () => this.applyPreset(id));
      this.presetButtons.set(id, btn);
      presets.appendChild(btn);
    }
    frag.appendChild(presets);

    // Copy current surface config to another surface
    const copyRow = document.createElement('div');
    copyRow.className = 'tuning-copy';
    const copyLabel = document.createElement('span');
    copyLabel.textContent = 'Copier vers :';
    const copySelect = document.createElement('select');
    for (const id of SURFACE_IDS) {
      const opt = document.createElement('option');
      opt.value = id;
      opt.textContent = SURFACE_LABELS[id];
      copySelect.appendChild(opt);
    }
    this.copySelect = copySelect;
    const copyBtn = document.createElement('button');
    copyBtn.type = 'button';
    copyBtn.textContent = 'Copier';
    copyBtn.addEventListener('click', () => this.copyToSurface());
    copyRow.append(copyLabel, copySelect, copyBtn);
    frag.appendChild(copyRow);

    // Sliders
    for (const section of SECTIONS) {
      const h = document.createElement('h3');
      h.textContent = section.title;
      frag.appendChild(h);
      for (const def of section.sliders) {
        frag.appendChild(this.buildSlider(def));
      }
    }

    // Affichage local (mode éco) — moins de pixels / moins d'images par
    // seconde = moins de charge GPU. Pensé pour les portables sur batterie,
    // dont le bridage énergétique cause des gels d'image (enquête du 19/07).
    // La simulation reste à 60 Hz : précision réseau et chrono inchangés.
    const displayTitle = document.createElement('h3');
    displayTitle.textContent = 'Affichage (local)';
    frag.appendChild(displayTitle);

    const resRow = document.createElement('div');
    resRow.className = 'tuning-copy';
    const resLabel = document.createElement('span');
    resLabel.textContent = 'Résolution du rendu :';
    const resSelect = document.createElement('select');
    for (const [value, label] of [
      ['1', '100 % (nette)'],
      ['0.75', '75 % (éco)'],
      ['0.5', '50 % (éco max)'],
    ] as const) {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      resSelect.appendChild(opt);
    }
    resSelect.value = String(this.display.renderScale);
    resSelect.addEventListener('change', () => {
      const v = Number(resSelect.value);
      this.display.renderScale = v === 0.5 || v === 0.75 ? v : 1;
      saveDisplaySettings(this.display);
    });
    resRow.append(resLabel, resSelect);
    frag.appendChild(resRow);

    const halfRow = document.createElement('label');
    halfRow.className = 'tuning-follow';
    const halfBox = document.createElement('input');
    halfBox.type = 'checkbox';
    halfBox.checked = this.display.halfRate;
    halfBox.addEventListener('change', () => {
      this.display.halfRate = halfBox.checked;
      saveDisplaySettings(this.display);
    });
    halfRow.append(
      halfBox,
      document.createTextNode(' Mode éco 30 img/s (simulation et chrono restent à 60 Hz)'),
    );
    frag.appendChild(halfRow);

    const gridRow = document.createElement('label');
    gridRow.className = 'tuning-follow';
    const gridBox = document.createElement('input');
    gridBox.type = 'checkbox';
    gridBox.checked = this.display.grid;
    gridBox.addEventListener('change', () => {
      this.display.grid = gridBox.checked;
      saveDisplaySettings(this.display);
    });
    gridRow.append(gridBox, document.createTextNode(' Grille hors piste (repère de vitesse)'));
    frag.appendChild(gridRow);

    const autoRow = document.createElement('label');
    autoRow.className = 'tuning-follow';
    const autoBox = document.createElement('input');
    autoBox.type = 'checkbox';
    autoBox.checked = this.display.auto;
    autoBox.addEventListener('change', () => {
      this.display.auto = autoBox.checked;
      saveDisplaySettings(this.display);
    });
    autoRow.append(
      autoBox,
      document.createTextNode(' Adaptation automatique (baisse la qualité si ça rame)'),
    );
    frag.appendChild(autoRow);

    // Debug : superpose le contour de collision exact (HULL_PATH du kit de
    // design) sur chaque auto-tamponneuse — pour vérifier d'un coup d'œil que
    // « ce qui se touche à l'écran » est bien ce que la physique juge.
    const hullRow = document.createElement('label');
    hullRow.className = 'tuning-follow';
    const hullBox = document.createElement('input');
    hullBox.type = 'checkbox';
    hullBox.checked = this.display.hullDebug;
    hullBox.addEventListener('change', () => {
      this.display.hullDebug = hullBox.checked;
      saveDisplaySettings(this.display);
    });
    hullRow.append(
      hullBox,
      document.createTextNode(' Contours de collision sur les voitures (debug)'),
    );
    frag.appendChild(hullRow);

    const displayHint = document.createElement('div');
    displayHint.className = 'tuning-flip-hint';
    displayHint.textContent =
      'Réglages de CET appareil (non partagés). L’adaptation automatique descend en éco quand le rendu souffre (« éco auto » sur la ligne FPS) et remonte après 30 s d’accalmie — jamais au-dessus de vos réglages manuels.';
    frag.appendChild(displayHint);

    // Notice shown to non-admins (their edits are local only; the server keeps
    // the shared configs — only admins can push them to everyone).
    const notice = document.createElement('div');
    notice.className = 'tuning-msg';
    notice.textContent = 'Réservé aux admins : vos réglages restent locaux.';
    notice.hidden = true;
    this.adminNotice = notice;
    frag.appendChild(notice);

    // Actions + IO
    const actions = document.createElement('div');
    actions.className = 'tuning-actions';
    const exportBtn = document.createElement('button');
    exportBtn.type = 'button';
    exportBtn.textContent = 'Exporter JSON';
    exportBtn.addEventListener('click', () => this.exportJson());
    const importBtn = document.createElement('button');
    importBtn.type = 'button';
    importBtn.textContent = 'Importer JSON';
    importBtn.addEventListener('click', () => this.importJson());
    const resetBtn = document.createElement('button');
    resetBtn.type = 'button';
    resetBtn.textContent = 'Réinitialiser';
    resetBtn.addEventListener('click', () => this.reset());
    actions.append(exportBtn, importBtn, resetBtn);
    frag.appendChild(actions);

    const io = document.createElement('textarea');
    io.id = 'tuning-io';
    io.placeholder =
      'Le JSON exporté (les 5 surfaces + caméra) apparaît ici ; collez-en un puis « Importer JSON ».';
    io.spellcheck = false;
    this.io = io;
    frag.appendChild(io);

    const msg = document.createElement('div');
    msg.className = 'tuning-msg';
    this.msg = msg;
    frag.appendChild(msg);

    this.root.appendChild(frag);
    this.selectSurface(this.selectedSurface);
  }

  private buildSlider(def: SliderDef): HTMLElement {
    const row = document.createElement('div');
    row.className = 'tuning-row';

    const label = document.createElement('label');
    label.textContent = def.label;
    const range = document.createElement('input');
    range.type = 'range';
    range.min = String(def.min);
    range.max = String(def.max);
    range.step = String(def.step);
    const value = document.createElement('span');
    value.className = 'tuning-value';

    // The written config is captured when the gesture starts: even if the
    // edited surface changed mid-drag, the whole drag lands on ONE config.
    let dragTarget: NumericRecord | null = null;
    range.addEventListener('pointerdown', () => {
      dragTarget = this.targetRecord(def.target);
    });
    window.addEventListener('pointerup', () => {
      dragTarget = null;
    });
    range.addEventListener('input', () => {
      const v = Number(range.value);
      const target = dragTarget ?? this.targetRecord(def.target);
      target[def.key] = v;
      value.textContent = formatValue(v);
      this.afterChange(def.target);
    });

    row.append(label, range, value);
    this.inputs.set(`${def.target}.${def.key}`, { range, value });
    return row;
  }

  private afterChange(target: SliderTarget): void {
    if (target === 'physics') {
      this.schedulePush();
      this.refreshPresetHighlight();
    } else if (target === 'camera') {
      this.persistCamera();
    } else {
      this.options.onNetSimChanged();
    }
  }

  /** Restores the edited surface to its per-surface default (the intended feel). */
  private applySurfaceDefault(): void {
    Object.assign(this.currentPhysics(), defaultSurfaceConfigs()[this.selectedSurface]);
    this.schedulePush();
    this.refresh();
    this.say(`Défauts de ${SURFACE_LABELS[this.selectedSurface]} restaurés.`);
  }

  private applyPreset(id: PresetId): void {
    const preset = PHYSICS_PRESETS[id];
    const target = asRecord(this.currentPhysics());
    for (const [key, val] of Object.entries(preset)) {
      target[key] = val;
    }
    this.schedulePush();
    this.refresh();
    this.say(`Preset « ${PRESET_LABELS[id]} » appliqué à ${SURFACE_LABELS[this.selectedSurface]}.`);
  }

  private copyToSurface(): void {
    if (!this.copySelect) return;
    const dest = this.copySelect.value as SurfaceId;
    if (dest === this.selectedSurface) {
      this.say('Choisissez une surface différente de celle éditée.');
      return;
    }
    const from = asRecord(this.currentPhysics());
    const to = asRecord(this.surfaces[dest]);
    for (const key of Object.keys(from)) {
      to[key] = from[key] as number;
    }
    this.schedulePush();
    this.say(
      `Réglages de ${SURFACE_LABELS[this.selectedSurface]} copiés vers ${SURFACE_LABELS[dest]}.`,
    );
  }

  private reset(): void {
    const defaults = defaultSurfaceConfigs();
    for (const id of SURFACE_IDS) {
      Object.assign(this.surfaces[id], defaults[id]);
    }
    Object.assign(this.camera, defaultCamera());
    this.persistCamera();
    this.schedulePush();
    this.refresh();
    this.say('Réglages réinitialisés (défauts des 5 surfaces).');
  }

  private exportJson(): void {
    const json = JSON.stringify({ surfaces: this.surfaces, camera: this.camera }, null, 2);
    if (this.io) this.io.value = json;
    void navigator.clipboard
      ?.writeText(json)
      .then(() => this.say('JSON exporté (copié dans le presse-papiers).'))
      .catch(() => this.say('JSON exporté dans la zone de texte.'));
  }

  private importJson(): void {
    if (!this.io || this.io.value.trim() === '') {
      this.say('Collez un JSON dans la zone de texte d’abord.');
      return;
    }
    try {
      const parsed: unknown = JSON.parse(this.io.value);
      const data = parsed as {
        surfaces?: Partial<Record<SurfaceId, NumericRecord>>;
        physics?: NumericRecord;
        camera?: NumericRecord;
      };
      let applied = 0;
      if (data.surfaces) {
        for (const id of SURFACE_IDS) {
          const source = data.surfaces[id];
          if (!source) continue;
          applied += this.applyPhysicsRecord(source, this.surfaces[id]);
        }
      }
      // v1 shape (single `physics` object): applied to the edited surface.
      if (data.physics) {
        applied += this.applyPhysicsRecord(data.physics, this.currentPhysics());
      }
      if (data.camera) {
        for (const section of SECTIONS) {
          for (const def of section.sliders) {
            if (def.target !== 'camera') continue;
            const v = data.camera[def.key];
            if (typeof v === 'number' && Number.isFinite(v)) {
              asRecord(this.camera)[def.key] = clampToRange(v, def);
              applied++;
            }
          }
        }
        this.persistCamera();
      }
      this.schedulePush();
      this.refresh();
      this.say(`Import réussi (${applied} paramètres appliqués).`);
    } catch {
      this.say('JSON invalide — import annulé.');
    }
  }

  /** Applies the physics slider keys of `source` onto `target`, clamped. */
  private applyPhysicsRecord(source: NumericRecord, target: PhysicsConfig): number {
    let applied = 0;
    for (const section of SECTIONS) {
      for (const def of section.sliders) {
        if (def.target !== 'physics') continue;
        const v = source[def.key];
        if (typeof v === 'number' && Number.isFinite(v)) {
          asRecord(target)[def.key] = clampToRange(v, def);
          applied++;
        }
      }
    }
    return applied;
  }

  private persistCamera(): void {
    localStorage.setItem(CAMERA_STORAGE_KEY, JSON.stringify(this.camera));
  }

  private restoreCamera(): void {
    const raw = localStorage.getItem(CAMERA_STORAGE_KEY);
    if (!raw) return;
    try {
      const data = JSON.parse(raw) as NumericRecord;
      for (const section of SECTIONS) {
        for (const def of section.sliders) {
          if (def.target !== 'camera') continue;
          const v = data[def.key];
          if (typeof v === 'number' && Number.isFinite(v)) {
            asRecord(this.camera)[def.key] = clampToRange(v, def);
          }
        }
      }
    } catch {
      localStorage.removeItem(CAMERA_STORAGE_KEY);
    }
  }

  /** Syncs every slider/value display with the edited surface + local configs. */
  private refresh(): void {
    for (const section of SECTIONS) {
      for (const def of section.sliders) {
        const entry = this.inputs.get(`${def.target}.${def.key}`);
        if (!entry) continue;
        const source = this.targetRecord(def.target);
        const v = source[def.key] ?? 0;
        entry.range.value = String(v);
        entry.value.textContent = formatValue(v);
      }
    }
    this.refreshPresetHighlight();
  }

  private refreshPresetHighlight(): void {
    for (const [id, btn] of this.presetButtons) {
      btn.classList.toggle('active', this.matchesPreset(id));
    }
    this.surfaceDefaultButton?.classList.toggle(
      'active',
      this.matchesConfig(defaultSurfaceConfigs()[this.selectedSurface]),
    );
  }

  private matchesPreset(id: PresetId): boolean {
    return this.matchesConfig(PHYSICS_PRESETS[id]);
  }

  private matchesConfig(reference: PhysicsConfig): boolean {
    const current = asRecord(this.currentPhysics());
    for (const [key, val] of Object.entries(reference)) {
      if (Math.abs((current[key] ?? 0) - val) > 1e-9) return false;
    }
    return true;
  }

  private say(text: string): void {
    if (this.msg) this.msg.textContent = text;
  }
}

function clampToRange(v: number, def: SliderDef): number {
  return v < def.min ? def.min : v > def.max ? def.max : v;
}

function formatValue(v: number): string {
  return Math.abs(v) >= 100 ? v.toFixed(0) : Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2);
}
